import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type WorkflowRunStatus =
  | 'queued'
  | 'running'
  | 'success'
  | 'failed'
  | 'inconclusive'
  | 'canceled'
  | 'timeout';

export type WorkflowRunStepTotals = {
  total: number;
  success: number;
  failed: number;
  /** Warn-severity (non-blocking) assertion failures; excluded from `failed`. */
  warned: number;
  waiting: number;
  running: number;
  skipped: number;
};

export type WorkflowRunSummaryDto = {
  id: string;
  status: WorkflowRunStatus;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  resumeAt: string | null;
  waitingNodeId: string | null;
  browserMode: 'headless' | 'headed' | null;
  stepTotals: WorkflowRunStepTotals;
  /**
   * How the run executed. `api` means it ran only its api_sequence (e.g. a seed
   * flow) and has NO browser steps — render `apiCalls` instead of `stepTotals`.
   */
  executionMode: 'api' | 'browser';
  /** api_sequence call totals for `executionMode === 'api'` runs (else null). */
  apiCalls: { total: number; success: number } | null;
  hasScreenshots: boolean;
  /** Published snapshot version (null for draft / legacy runs) */
  snapshotVersion: number | null;
  /** Draft snapshot info (null for published / legacy runs) */
  draftSnapshot: {
    id: string;
    version: string;
    basePublishedVersion: number;
  } | null;
  /** Immutable numbered revision pinned by canonical runs. */
  workflowRevision?: { id: string; number: number } | null;
  /** Scenario that supplied input values (null if none used) */
  scenarioId: string | null;
  scenarioName: string | null;
};

export type Pagination = {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

type WorkflowRunsResponse<Row> = {
  ok: true;
  data: { runs: Row[] };
  pagination: Pagination;
};

/** An optimization run projected into a flow's optimization history. */
export type ConvergenceRunHistoryRow = {
  kind: 'convergence';
  id: string;
  status: string;
  workflowId: string;
  phase: string;
  outcome: string | null;
  cycleCount: number;
  cycleCap: number | null;
  cacheOnlyRunCount: number;
  modelId: string | null;
  /** What started this run — 'manual' | 'assistant' | 'autobuild' | 'flaky' | 'slow' | 'app_changed' | 'recache' | 'edited' | 'seed_dependency'. */
  trigger: string;
  triggeredBy: { id: string; displayName: string | null; email: string } | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

/** Shared fetch body for the native and optimization run list queries. */
async function fetchWorkflowRuns<Row>(
  workflowId: string,
  params: {
    page: number;
    pageSize: number;
    mode?: 'browser' | 'api';
    runKind?: 'convergence';
  }
): Promise<WorkflowRunsResponse<Row>> {
  const url = new URL(`${API_URL}/workflows/${workflowId}/runs`);
  url.searchParams.set('page', String(params.page));
  url.searchParams.set('pageSize', String(params.pageSize));
  if (params.mode) url.searchParams.set('mode', params.mode);
  if (params.runKind) url.searchParams.set('runKind', params.runKind);

  const r = await fetch(url.toString(), { credentials: 'include' });
  // 404 is expected when the workflow doesn't exist for the viewer
  // (e.g. switching orgs in another tab) — return empty instead of throwing.
  if (r.status === 404) {
    return {
      ok: true,
      data: { runs: [] },
      pagination: {
        page: params.page,
        pageSize: params.pageSize,
        totalItems: 0,
        totalPages: 0,
      },
    } satisfies WorkflowRunsResponse<Row>;
  }
  if (!r.ok) throw await toApiError(r, 'Failed to fetch workflow runs');
  const data = (await r.json()) as WorkflowRunsResponse<Row>;
  if (!data.ok) throw new Error('Failed to fetch workflow runs');
  return data;
}

export function useWorkflowRuns(
  workflowId: string | null,
  params: { page: number; pageSize: number; mode?: 'browser' | 'api' }
) {
  return useQuery({
    queryKey: ['workflow-runs', workflowId, params.page, params.pageSize, params.mode],
    enabled: Boolean(workflowId),
    queryFn: async () => {
      if (!workflowId) throw new Error('Missing workflowId');
      return fetchWorkflowRuns<WorkflowRunSummaryDto>(workflowId, params);
    },
  });
}

/** Fetches only optimization runs, without the expensive merged-runs path. */
export function useOptimizationRuns(
  workflowId: string | null,
  params: {
    page: number;
    pageSize: number;
  },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['optimization-runs', workflowId, params.page, params.pageSize],
    enabled: Boolean(workflowId) && (options?.enabled ?? true),
    queryFn: async () => {
      if (!workflowId) throw new Error('Missing workflowId');
      return fetchWorkflowRuns<ConvergenceRunHistoryRow>(workflowId, {
        ...params,
        runKind: 'convergence',
      });
    },
  });
}

export function useDeleteWorkflowRun(workflowId: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (runId: string) => {
      if (!workflowId) throw new Error('Missing workflowId');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to delete workflow run');
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-runs', workflowId] });
      qc.invalidateQueries({ queryKey: ['optimization-runs', workflowId] });
    },
  });
}
